import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// --- Thunk to mark out and upload data ---
export const markOut = createAsyncThunk(
    "session/markOut",
    async (_, { getState, dispatch }) => {
        try {
            const state = getState();
            // ✅ Always pull from localStorage to ensure persistence
            const energyState = JSON.parse(localStorage.getItem("energySoundState"));
            const images = JSON.parse(localStorage.getItem("shiftScreenshots")) || [];
            const authState = state.auth;

            const formData = new FormData();

            // 🔹 Rebuild compressed images like uploadAllDataToServer
            for (let i = 0; i < images.length; i++) {
                const image = images[i];
                const response = await fetch(image.data);
                const blob = await response.blob();

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                await new Promise((resolve) => {
                    img.onload = () => {
                        canvas.width = img.width;
                        canvas.height = img.height;
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob((compressedBlob) => {
                            formData.append('screenshots', compressedBlob, `screenshot_slot_${image.hourSlot}_${image.id}.jpg`);
                            resolve();
                        }, 'image/jpeg', 0.8);
                    };
                    img.src = image.data;
                });
            }


            // 🔹 Build session payload
            const payloadData = {
                sessionStartTime: energyState.sessionStartTime,
                sessionEndTime: new Date().toISOString(),
                totalHours: Math.min(energyState.currentSlot - 1, 10),
                stopLogs: energyState.stopLogs,
                productivitySlots: energyState.productivitySlots,
                totalScreenshots: images.length,
                adminId: authState?.current?._id || null,
            };

            formData.append("sessionData", JSON.stringify(payloadData));

            // 🔹 Upload everything together
            const uploadResponse = await axios.post("/upload-screenshot", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (uploadResponse.status === 200) {
                const updatedImages = images.map((img) => ({ ...img, uploaded: true }));
                localStorage.setItem("shiftScreenshots", JSON.stringify(updatedImages));
                localStorage.setItem(
                    "screenShortRecordId",
                    uploadResponse.data.data.recordId
                );
                localStorage.setItem(
                    "sessionUploadStatus",
                    JSON.stringify({
                        uploaded: true,
                        uploadTime: new Date().toISOString(),
                        recordId: uploadResponse.data.data.recordId,
                    })
                );

                console.log("✅ Session uploaded successfully:", payloadData);
            }
        } catch (err) {
            console.error("❌ Failed to upload session:", err);
        }

        dispatch(sessionSlice.actions.resetSession());
    }
);

const sessionSlice = createSlice({
    name: "session",
    initialState: {
        sessionStartTime: null,
        currentSlot: 1,
        stopLogs: [],
        productivitySlots: [],
        images: [],
        isMarkedOut: false,
    },
    reducers: {
        setSessionStartTime: (state, action) => {
            state.sessionStartTime = action.payload;
            localStorage.setItem("sessionStartTime", action.payload);
        },
        setCurrentSlot: (state, action) => {
            state.currentSlot = action.payload;
            localStorage.setItem("currentSlot", action.payload);
        },
        addStopLog: (state, action) => {
            state.stopLogs.push(action.payload);
            localStorage.setItem("stopLogs", JSON.stringify(state.stopLogs));
        },
        addProductivitySlot: (state, action) => {
            state.productivitySlots.push(action.payload);
            localStorage.setItem("productivitySlots", JSON.stringify(state.productivitySlots));
        },
        addImage: (state, action) => {
            state.images.push(action.payload);
            localStorage.setItem("shiftScreenshots", JSON.stringify(state.images));
        },
        resetSession: (state) => {
            state.sessionStartTime = null;
            state.currentSlot = 1;
            state.stopLogs = [];
            state.productivitySlots = [];
            state.images = [];
            state.isMarkedOut = true;
            localStorage.clear();
        },
    },
});

export const {
    setSessionStartTime,
    setCurrentSlot,
    addStopLog,
    addProductivitySlot,
    addImage,
    resetSession,
} = sessionSlice.actions;

export default sessionSlice.reducer;
